////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: News teaser card — clipWipe image reveal + hover grammar        //
//           (1.0.3.md §6)                                                   //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Lives INSIDE a caller's own <GlassTile> grid (§5's perf guardrail: "a dense
//  grid of many small tiles should share ONE glass surface" — this card is
//  plain, bordered content, not a second nested glass surface per card).
//
////////////////////////////////////////////////////////////////////////////////

import { Link } from 'react-router';
import { motion } from 'framer-motion';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { cardHover, cardHoverImage, clipWipe, fadeRise, reduceHover, reduceVariants } from '@/lib/motion';
import { formatDateShort } from '@/lib/format';
import { Img } from '@/components/media/Img';

export interface NewsTeaserCardProps {
  slug: string;
  title: string;
  publishedAt: string;
  image: string | null;
}

export function NewsTeaserCard ({ slug, title, publishedAt, image }: NewsTeaserCardProps) {
  const reducedMotion = useReducedMotion();

  return (
    <motion.li
      className="overflow-hidden rounded-2xl border border-line"
      // `variants` (no own `initial`/`whileInView`) — this card is meant to be
      // used as a direct child of a <RevealGroup> (components/content/Reveal.tsx),
      // whose `staggerGroup` context this inherits so the whole card fades/rises
      // in on the group's beat, on top of the image's own independent clipWipe
      // reveal below.
      variants={reduceVariants(fadeRise, reducedMotion)}
      whileHover={reduceHover(cardHover, reducedMotion)}
    >
      <Link to={`/news/${slug}`} className="block">
        {image && (
          // MARK — 1.0.3 post-review tweak #5: no longer carries its own
          // `initial`/`whileInView`/`viewport` — see Home.tsx's ProjectTeaserCard
          // for the full root-cause note. This div now inherits the "hidden"/
          // "visible" state propagated by whichever <RevealGroup> ancestor the
          // caller wraps this card in, via `variants={clipWipe}` alone.
          <motion.div
            className="overflow-hidden"
            variants={reduceVariants(clipWipe, reducedMotion)}
          >
            <motion.div whileHover={reduceHover(cardHoverImage, reducedMotion)}>
              {/* alt="" deliberately — the whole card is one <Link>; its
                  accessible name should be the title text below, not the
                  image description duplicated in front of it. */}
              <Img src={image} alt="" className="h-48 w-full object-cover" />
            </motion.div>
          </motion.div>
        )}
        <div className="p-4">
          <p className="text-sm text-muted">{formatDateShort(publishedAt)}</p>
          <p className="mt-1 font-medium text-ink">{title}</p>
        </div>
      </Link>
    </motion.li>
  );
}
